import classNames from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";

import { validateNumber } from "@repo/shopify/utils/regex";

import { fetchUpdateCustomer } from "@api/lambdaApi";

import Button from "@atoms/Button";
import Dim from "@atoms/Dim";
import Icon from "@atoms/Icon";
import Input from "@atoms/Input";
import Link from "@atoms/Link";
import Select from "@atoms/Select";

import { NOTIFICATIONS } from "@const/notifications";
import type { IRecommendResult, ISizeGuide, TGender } from "@const/sizeRecommender";
import { loadSizeGuide, normalizeGender, recommend, resolveAvailableSize, toStoredGender } from "@const/sizeRecommender";

import useCustomerQuery from "@hooks/useCustomerQuery";
import useLanguage from "@hooks/useLanguage";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

interface IProps {
  productType: string | undefined;
  /** 고객 메타필드의 성별 원본값. 비로그인/미입력이면 undefined 또는 null. */
  gender?: string | null;
  height?: string | null;
  weight?: string | null;
  /** 이 상품이 실제로 갖고 있는 사이즈 옵션 값들. 추천 사이즈를 실제 구매 가능한 사이즈로 보정하는 데 쓰임 */
  availableSizes: string[];
  onClose: () => void;
  onApply: (size: string) => void;
}

/** 선호 핏 — 막대바 3눈금이 그대로 사이즈 데이터의 세 핏에 매핑 */
type TFit = keyof IRecommendResult["sizes"]; // "to" | "regular" | "relaxed"

const FIT_STEPS: { key: TFit; label: string }[] = [
  { key: "to", label: "Fitted" },
  { key: "regular", label: "Regular" },
  { key: "relaxed", label: "Loose" },
];

const GENDER_OPTIONS: { name: string; value: TGender | "" }[] = [
  { name: "Select Gender", value: "" },
  { name: "Male", value: "M" },
  { name: "Female", value: "F" },
];

interface IStepOneFormData {
  gender: TGender | "";
  height: string;
  weight: string;
}

export default function SizeRecommender({
  productType,
  gender: genderProp,
  height: heightProp,
  weight: weightProp,
  availableSizes,
  onClose,
  onApply,
}: IProps) {
  const initGender = useMemo(() => normalizeGender(genderProp), [genderProp]);
  const initHeight = heightProp !== null && heightProp !== undefined ? String(heightProp) : "";
  const initWeight = weightProp !== null && weightProp !== undefined ? String(weightProp) : "";

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [fitIndex, setFitIndex] = useState<number>(1); // 기본 Regular(가운데)
  const [guide, setGuide] = useState<ISizeGuide | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [result, setResult] = useState<IRecommendResult | null>(null);

  const { customer, token, update } = useCustomerStore();
  const { lang } = useLanguage();
  const { fetchCustomer } = useCustomerQuery();
  const { openToast } = useLayoutStore();

  const {
    register,
    handleSubmit,
    getValues,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm<IStepOneFormData>({
    defaultValues: { gender: initGender ?? "", height: initHeight, weight: initWeight },
    reValidateMode: "onSubmit",
  });

  // defaultValues는 mount 시점에만 반영되므로, 저장 후 customer가 갱신되면 폼도 다시 맞춰준다.
  useEffect(() => {
    reset({ gender: initGender ?? "", height: initHeight, weight: initWeight });
  }, [initGender, initHeight, initWeight, reset]);

  useEffect(() => {
    loadSizeGuide().then((data) => {
      setGuide(data);
    });
  }, []);

  const selectedFit = FIT_STEPS[fitIndex].key;

  // 추천값("XS or S" 등 복수 후보 포함)을 이 상품이 실제로 갖고 있는 사이즈로 보정
  const resolvedSize = useMemo(
    () => (result ? resolveAvailableSize(result.sizes[selectedFit], availableSizes) : null),
    [result, selectedFit, availableSizes],
  );

  // Step 1: 성별·키·몸무게 검증만 하고 Step 2로. 저장은 하지 않음
  const onStepOneSubmit: SubmitHandler<IStepOneFormData> = () => {
    setResult(null); // 입력이 갱신됐으므로 이전 추천 초기화
    setStep(2);
  };

  // Step 2: '추천 사이즈 보기' — 여기서만 저장 + 추천 표시가 일어난다.
  const handleShowResult = async () => {
    const { gender, height, weight } = getValues();
    if (!gender || !guide) return;

    // 1) 추천 계산 (로그인 여부 무관)
    const recommended = recommend(guide, {
      gender,
      productType: productType ?? "",
      heightCm: Number(height),
      weightKg: Number(weight),
    });
    setResult(recommended);

    // 추천 결과가 나오면 Step 3(결과 화면)로 이동. 저장 성공 여부와 무관.
    if (recommended) {
      setStep(3);
    }

    // 2) 저장 — 값이 바뀌었고 로그인 고객일 때만. (결과 표시를 막지 않음)
    const isChanged = gender !== initGender || height !== initHeight || weight !== initWeight;
    if (!isChanged || !customer.id) return;

    setIsSaving(true);
    try {
      const isUpdated = await fetchUpdateCustomer({
        domain: `thisisneverthat-${lang}`,
        customerId: customer.id,
        gender: toStoredGender(gender),
        height,
        weight,
      });

      if (isUpdated) {
        await fetchCustomer({ variables: { customerAccessToken: token.accessToken } });

        // fetchCustomer의 refetch가 방금 저장한 값을 아직 못 읽어오는 경우(백엔드 반영 지연)를 방어하기 위해
        // 방금 저장에 성공한 값을 store에 직접 덮어써서, 재오픈 시 항상 최신 입력값이 보이게 함
        update({
          ...useCustomerStore.getState().customer,
          gender: { ...useCustomerStore.getState().customer.gender, value: toStoredGender(gender) },
          height: { ...useCustomerStore.getState().customer.height, value: height },
          weight: { ...useCustomerStore.getState().customer.weight, value: weight },
        } as typeof customer);

        openToast(NOTIFICATIONS.SUCCESS_MODIFY_ACCOUNT_INFO);
      } else {
        openToast(NOTIFICATIONS.ERROR_SERVER);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      openToast(NOTIFICATIONS.ERROR_SERVER);
      throw new Error(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <section
        className={classNames(
          "pc:right-0 pc:z-60 pc:h-auto pc:max-h-dvh pc:left-auto pc:w-screen-col-span-right-3 pc:px-5 pc:pt-5 pc:pb-5 pc:gap-5 fixed top-0 left-0 z-50 flex h-dvh w-full flex-col gap-4 bg-white px-4 pt-0 pb-4 dark:bg-black dark:text-white",
        )}
      >
        <div className="pc:pt-0 pc:pb-0 flex items-center justify-between pt-3.5 pb-4 leading-none">
          <div className="flex items-center gap-6">
            <Button
              className="pc:hidden"
              onClick={() => {
                if (step === 1) {
                  onClose();
                  return;
                }
                setStep((prev) => (prev - 1) as 1 | 2);
              }}
            >
              <Icon className="h-3.5" name="LeftArrow" />
            </Button>
            {step === 1 ? (
              <h3 className="pc:text-sm text-lg">Size Recommender</h3>
            ) : (
              <Button
                className="pc:text-sm flex items-center gap-2.5 text-lg"
                onClick={() => {
                  setStep((prev) => (prev - 1) as 1 | 2);
                }}
              >
                <Icon className="pc:block hidden h-3.5" name="LeftArrow" />
                <span>Back</span>
              </Button>
            )}
          </div>

          <div>
            <Button className="pc:hidden" onClick={onClose}>
              <Icon name="Close" />
            </Button>
            <Button className="pc:inline-flex hidden text-sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>

        {/* ── Step 1: 성별 / 키 / 몸무게 ── */}
        {step === 1 ? (
          <div className="pc:flex-none flex flex-1 flex-col">
            <span className="font-fuzzy-bold pc:text-[1.125rem] pb-10 text-[1.375rem] leading-none">Step 1: Your Details</span>
            <form
              className="pc:pb-5 flex flex-col gap-2.5 pb-0 text-sm"
              id="size-recommender-form"
              onSubmit={handleSubmit(onStepOneSubmit)}
            >
              <Select
                as="square"
                id="size-recommender-gender"
                isError={Boolean(errors.gender)}
                options={GENDER_OPTIONS}
                {...register("gender", {
                  required: { value: true, message: "Please select your gender" },
                  onChange: () => {
                    clearErrors("gender");
                  },
                })}
              />
              {errors.gender?.message ? (
                <span className="text-red font-fuzzy-light -mt-1 leading-none">{errors.gender.message}</span>
              ) : null}

              <Input
                errorMessage={errors.height?.message}
                isError={Boolean(errors.height)}
                {...register("height", {
                  required: { value: true, message: "Please enter your height" },
                  validate: (value) => validateNumber(value) || "Please enter numbers only",
                  onChange: () => {
                    clearErrors("height");
                  },
                })}
                id="size-recommender-height"
                label="Height (cm)"
                placeholder="e.g. 170"
              />
              <Input
                errorMessage={errors.weight?.message}
                isError={Boolean(errors.weight)}
                {...register("weight", {
                  required: { value: true, message: "Please enter your weight" },
                  validate: (value) => validateNumber(value) || "Please enter numbers only",
                  onChange: () => {
                    clearErrors("weight");
                  },
                })}
                id="size-recommender-weight"
                label="Weight (kg)"
                placeholder="e.g. 80"
              />
            </form>
            <div className="pc:mt-0 mt-auto flex flex-col gap-5">
              <Button
                as="square"
                className="pc:text-sm text-md pc:h-7.5 h-10 w-full items-center justify-center leading-none"
                form="size-recommender-form"
                type="submit"
              >
                Continue
              </Button>
              <p className="text-dark-grey font-fuzzy-light pc:text-sm text-md">
                By using the recommender, you agree to the processing of your personal data in accordance with our{" "}
                <Link as="text" className="underline" href="https://intl.thisisneverthat.com/privacy-policy">
                  Privacy and Cookies Policy
                </Link>
              </p>
            </div>
          </div>
        ) : null}

        {/* ── Step 2: 선호 핏 선택 → 추천 사이즈 보기 ── */}
        {step === 2 ? (
          <div className="pc:flex-none pc:gap-5 flex flex-1 flex-col">
            <div className="flex flex-col gap-2.5">
              <span className="font-fuzzy-bold pc:text-[1.125rem] pb-10 text-[1.375rem] leading-none">Step 2: Fit Preference</span>
              <div className="pc:text-sm pc:flex-row pc:gap-0 pc:justify-between flex flex-col gap-5 text-lg">
                {FIT_STEPS.map((fit, idx) => (
                  <Input
                    checked={fitIndex === idx}
                    id={`size-recommender-fit-${fit.key}`}
                    key={fit.key}
                    label={fit.label}
                    name="size-recommender-fit"
                    onChange={() => {
                      setFitIndex(idx);
                    }}
                    type="radio"
                    value={fit.key}
                  />
                ))}
              </div>
            </div>

            <div className="pc:mt-0 mt-auto">
              <Button
                as="square"
                className="pc:text-sm text-md pc:h-7.5 h-10 w-full items-center justify-center leading-none"
                disabled={isSaving}
                onClick={() => {
                  void handleShowResult();
                }}
              >
                View recommendation
              </Button>
            </div>
          </div>
        ) : null}

        {/* ── Step 3: 추천 결과 ── */}
        {step === 3 && result && resolvedSize ? (
          <div className="pc:flex-none flex flex-1 flex-col">
            <span className="font-fuzzy-bold pc:text-[1.125rem] pb-10 text-[1.375rem] leading-none">
              Your Recommended Size is {resolvedSize}
            </span>
            <div className="pc:mt-0 mt-auto">
              <Button
                as="square"
                className="pc:text-sm text-md pc:h-7.5 h-10 w-full items-center justify-center leading-none"
                onClick={() => {
                  onApply(resolvedSize);
                  onClose();
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        ) : null}
      </section>
      <Dim onClick={onClose} />
    </>
  );
}
